import { useState } from "react";
import { API, apiCall } from "../api";
import { trackEvent } from "../analytics";
import { usePromo } from "../utils/promo";

// Rate Tenant Component
const RateTenant = () => {
  const [formData, setFormData] = useState({
    tenant_name: '',
    tenant_email: '',
    tenant_phone: '',
    payment_reliability: 5,
    property_care: 5,
    communication: 5,
    cleanliness: 5,
    noise_level: 5,
    lease_compliance: 5,
    overall_rating: 5,
    comments: '',
    rental_period_start: '',
    rental_period_end: '',
    property_address: ''
  });
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [hideAddress, setHideAddress] = useState(false);
  const promo = usePromo();

  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files);
    
    // Validate file count
    if (uploadedFiles.length + fileArray.length > 5) {
      alert('M\u00e1ximo 5 archivos permitidos por evaluaci\u00f3n');
      return;
    }

    // Validate file types
    const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx', '.txt'];
    const invalidFiles = fileArray.filter(file => {
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      return !allowedTypes.includes(fileExt);
    });

    if (invalidFiles.length > 0) {
      alert(`Tipos de archivo no permitidos: ${invalidFiles.map(f => f.name).join(', ')}\nFormatos permitidos: PDF, JPG, PNG, GIF, DOC, DOCX, TXT`);
      return;
    }

    // Validate file sizes (10MB limit)
    const oversizedFiles = fileArray.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert(`Archivos demasiado grandes (m\u00e1ximo 10MB): ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }

    setUploadLoading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      fileArray.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API}/files/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setUploadedFiles(prev => [...prev, ...result.files]);
        alert(`${result.files.length} archivo(s) subido(s) exitosamente`);
      } else {
        const error = await response.json();
        alert(`Error al subir archivos: ${error.detail || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error al subir archivos. Por favor intenta de nuevo.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const removeFile = async (fileId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
      } else {
        const error = await response.json();
        alert(`Error al eliminar archivo: ${error.detail || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error al eliminar archivo');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDirectRating = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPaymentStatus('');

    try {
      const token = localStorage.getItem('token');
      
      // Prepare tenant data with uploaded files
      const tenantDataWithFiles = {
        ...formData,
        property_address: hideAddress ? 'No especificada' : formData.property_address,
        evidence_files: uploadedFiles.map(file => ({
          id: file.id,
          filename: file.filename,
          content_type: file.content_type,
          size: file.size,
          upload_date: file.upload_date
        }))
      };
      
      if (!promo.free_mode) {
        // Paid mode: Stripe checkout registers the rating once the payment goes through
        const checkout = await apiCall('/payments/checkout/session', 'POST', {
          tenant_data: tenantDataWithFiles,
          origin_url: window.location.origin
        }, token);
        window.location.href = checkout.url;
        return;
      }

      const result = await apiCall('/tenants/register-rating', 'POST', tenantDataWithFiles, token);

      if (result.message) {
        trackEvent('rating_submitted');
        setPaymentStatus('\u00a1Calificaci\u00f3n enviada exitosamente!');
        // Reset form
        setFormData({
          tenant_name: '',
          tenant_email: '',
          tenant_phone: '',
          property_address: '',
          rental_period_start: '',
          rental_period_end: '',
          payment_reliability: 5,
          property_care: 5,
          communication: 5,
          cleanliness: 5,
          noise_level: 5,
          lease_compliance: 5,
          overall_rating: 5,
          comments: ''
        });
        setUploadedFiles([]);
        setHideAddress(false);
      }
    } catch (error) {
      console.error('Rating submission failed:', error);
      setPaymentStatus(error.detail || 'Error al enviar la calificaci\u00f3n. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const RatingSlider = ({ label, value, onChange }) => {
    const labels = {
      1: 'Muy malo',
      2: 'Malo',
      3: 'Regular',
      4: 'Bueno',
      5: 'Excelente'
    };
    const colors = {
      1: 'text-red-500',
      2: 'text-orange-500',
      3: 'text-yellow-500',
      4: 'text-lime-500',
      5: 'text-green-500'
    };
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
          {label}: <span className={`font-bold ${colors[value] || 'text-gray-500'}`}>{labels[value] || ''} ({value}/5)</span>
        </label>
        <div className="flex gap-1 justify-center">
          {[1, 2, 3, 4, 5].map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => onChange(num)}
              className={`transition-all duration-150 transform hover:scale-110 active:scale-95 ${
                num <= value ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-200'
              }`}
              aria-label={`${num} estrellas`}
            >
              <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.539 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Calificar un Inquilino</h2>
      
      <form onSubmit={handleDirectRating} className="space-y-6">
        {/* Tenant Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Informaci&oacute;n del Inquilino</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Inquilino*</label>
              <input
                type="text"
                required
                value={formData.tenant_name}
                onChange={(e) => setFormData(prev => ({ ...prev, tenant_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Juan P&eacute;rez"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Correo Electr&oacute;nico</label>
              <input
                type="email"
                value={formData.tenant_email}
                onChange={(e) => setFormData(prev => ({ ...prev, tenant_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="juan@correo.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tel&eacute;fono</label>
              <input
                type="tel"
                value={formData.tenant_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, tenant_phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="+34 123 456 789"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Direcci&oacute;n de la Propiedad{hideAddress ? '' : '*'}</label>
              <input
                type="text"
                required={!hideAddress}
                disabled={hideAddress}
                value={hideAddress ? '' : formData.property_address}
                onChange={(e) => setFormData(prev => ({ ...prev, property_address: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder={hideAddress ? 'No especificada' : 'Calle Mayor 123, Madrid, Espa\u00f1a'}
              />
              <label className="mt-2 flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={hideAddress}
                  onChange={(e) => setHideAddress(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Prefiero no especificar la direcci&oacute;n de la propiedad
              </label>
            </div>
          </div>
        </div>

        {/* Rental Period */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Per&iacute;odo de Alquiler</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Inicio*</label>
              <input
                type="date"
                required
                value={formData.rental_period_start}
                onChange={(e) => setFormData(prev => ({ ...prev, rental_period_start: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Fin*</label>
              <input
                type="date"
                required
                value={formData.rental_period_end}
                onChange={(e) => setFormData(prev => ({ ...prev, rental_period_end: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Ratings */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Calificar al Inquilino</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <RatingSlider
                label="Confiabilidad de Pago"
                value={formData.payment_reliability}
                onChange={(value) => setFormData(prev => ({ ...prev, payment_reliability: value }))}
              />
              <RatingSlider
                label="Cuidado de la Propiedad"
                value={formData.property_care}
                onChange={(value) => setFormData(prev => ({ ...prev, property_care: value }))}
              />
              <RatingSlider
                label="Comunicaci&oacute;n"
                value={formData.communication}
                onChange={(value) => setFormData(prev => ({ ...prev, communication: value }))}
              />
            </div>
            <div>
              <RatingSlider
                label="Limpieza"
                value={formData.cleanliness}
                onChange={(value) => setFormData(prev => ({ ...prev, cleanliness: value }))}
              />
              <RatingSlider
                label="Nivel de Ruido (5 = Silencioso)"
                value={formData.noise_level}
                onChange={(value) => setFormData(prev => ({ ...prev, noise_level: value }))}
              />
              <RatingSlider
                label="Cumplimiento del Contrato"
                value={formData.lease_compliance}
                onChange={(value) => setFormData(prev => ({ ...prev, lease_compliance: value }))}
              />
            </div>
          </div>
          <RatingSlider
            label="Calificaci&oacute;n General"
            value={formData.overall_rating}
            onChange={(value) => setFormData(prev => ({ ...prev, overall_rating: value }))}
          />
        </div>

        {/* Comments */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Comentarios*</label>
          <textarea
            required
            rows={4}
            value={formData.comments}
            onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder="Describe tu experiencia con este inquilino..."
          />
        </div>

        {/* File Upload Section */}
        <div className="bg-amber-50 p-6 rounded-lg border border-amber-200">
          <h3 className="text-lg font-medium text-amber-800 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            Archivos de Evidencia (Opcional)
          </h3>
          <p className="text-sm text-amber-700 mb-4">
            Sube documentos, fotos, contratos u otros archivos como prueba de tu evaluaci&oacute;n.
            <br />
            <strong>Formatos permitidos:</strong> PDF, JPG, PNG, GIF, DOC, DOCX, TXT
            <br />
            <strong>Tama&ntilde;o m&aacute;ximo:</strong> 10MB por archivo | <strong>M&aacute;ximo:</strong> 5 archivos
          </p>

          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive 
                ? 'border-amber-400 bg-amber-100' 
                : 'border-amber-300 hover:border-amber-400 hover:bg-amber-100'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt"
              onChange={handleFileInputChange}
              className="hidden"
              id="file-upload"
              disabled={uploadLoading || uploadedFiles.length >= 5}
            />
            
            {uploadLoading ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mb-2"></div>
                <p className="text-amber-700 font-medium">Subiendo archivos...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <svg className="w-12 h-12 text-amber-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-amber-800 font-medium mb-2">
                  Arrastra archivos aqu&iacute; o{' '}
                  <label htmlFor="file-upload" className="text-amber-600 hover:text-amber-700 cursor-pointer underline">
                    selecciona archivos
                  </label>
                </p>
                <p className="text-sm text-amber-600">
                  {uploadedFiles.length}/5 archivos subidos
                </p>
              </div>
            )}
          </div>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-amber-800 mb-2">Archivos Subidos:</h4>
              <div className="space-y-2">
                {uploadedFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-200">
                    <div className="flex items-center flex-1">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                        {file.content_type?.includes('image') ? (
                          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        ) : file.content_type?.includes('pdf') ? (
                          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.filename}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(file.id)}
                      className="ml-3 p-1 text-red-400 hover:text-red-600 transition-colors"
                      title="Eliminar archivo"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {paymentStatus && (
          <div className={`border px-4 py-3 rounded-lg ${
            paymentStatus.includes('exitosamente') ? 
              'bg-green-50 border-green-200 text-green-700' : 
              'bg-red-50 border-red-200 text-red-700'
          }`}>
            {paymentStatus}
          </div>
        )}

        {uploadedFiles.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-700 text-sm">
              <strong>Archivos incluidos:</strong> {uploadedFiles.length} archivo(s) de evidencia ser&aacute;n adjuntados a esta evaluaci&oacute;n.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || uploadLoading || !promo.loaded}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition duration-200"
        >
          {loading ? 'Procesando...' : uploadLoading ? 'Subiendo archivos...'
            : promo.free_mode ? 'Enviar Calificaci\u00f3n (gratis)' : `Pagar $${promo.rating_price_usd} USD y Enviar`}
        </button>
      </form>
    </div>
  );
};

export { RateTenant };
